import styled from '@emotion/styled'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { HandwrittenNoteType } from '~/@common/api/handwrittenNoteApi'
import { routeName } from '~/@common/constants'
import { TOAST_STRING } from '~/@common/constants/strings'
import { useLearningTime } from '~/@common/hooks/useLearningTime'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { EXAM_WRITE_KEY, resolveViewerMode } from '~/@common/models/(Permission)/permissionUtils'
import { errorHandlerService, WorksheetProblemNoteService } from '~/@common/services'
import { learningContentLastScreenService } from '~/@common/services/learningContentLastScreen/LearningContentLastScreen.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import WorksheetScoringByOne from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne'
import WorksheetScoringByOneMobile from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne.mobile'
import { ScoringSubmitConfirmModal } from '~/@pages/student/@widgets/StudentWorksheetScoring/ScoringSubmitConfirmModal'
import { useLearningProcessReference } from '~/@pages/student/learning-process/@common/hooks/useGetLearningProcessReference'
import { WorksheetQuickScoring } from '~/@pages/qr-worksheet/$worksheetId/$dateTime/scoring/@widgets/WorksheetQuickScoring'

import { StudentExamPageService } from './@service/StudentExamPage.service'
import { ExamPageHeader } from './@widgets/ExamPageHeader'

const StudentExamByIdScoringPage = observer(() => {
  const isParentView = resolveViewerMode(commonRepo.permission, EXAM_WRITE_KEY) === 'parent'
  useLearningTime(undefined, undefined, !isParentView)
  const studentExamPageService = useRepository(StudentExamPageService)
  const { studentExamId } = useParams<{ studentExamId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMobile } = useStudentAppMediaQuery()
  const navigate = useNavigate()

  const noteService = useRepository(WorksheetProblemNoteService)
  const referenceParam = useLearningProcessReference()

  const scoringSize = searchParams.get('size') as 'all' | 'one'

  const confirmToSubmitAnswers = async () => {
    modalService.openModal(
      <ScoringSubmitConfirmModal
        onClick={async () => {
          if (!studentExamId) {
            return
          }
          try {
            modalService.showLoader()
            modalService.closeModal()

            const examProblemIds = studentExamPageService.problemScoringColl?.toScoredArr.map(
              (item) => item.id,
            )

            try {
              await noteService.uploadNotes(
                Number(studentExamId),
                examProblemIds,
                HandwrittenNoteType.STUDENT_EXAM_SCORING,
              )
              window.freshpaint?.track('필기 제출', { ...referenceParam })
            } catch (err) {
              errorHandlerService.handle(err, {
                message: TOAST_STRING.saveHandwrittenNoteFailed,
                useRemoteLogging: true,
              })
            }

            await studentExamPageService.submitAutoScoringExamProblemsAndSolveExam(studentExamId)
            navigate(routeName.student.studentWorksheet)
          } catch (error) {
            errorHandlerService.handle(error)
          } finally {
            modalService.hideLoader()
          }
        }}
      />,
      {
        modalName: 'ScoringSubmitConfirmModal',
      },
    )
  }

  useEffect(() => {
    if (!studentExamId) {
      return
    }
    ;(async () => {
      try {
        await studentExamPageService.loadStudentExamScoring(studentExamId)
        if (
          studentExamPageService.studentExamWorksheet?.status === 'COMPLETE' &&
          studentExamPageService.exam?.scheduleProgress !== 'GRADING' &&
          studentExamPageService.exam?.scheduleProgress !== 'RESOLVED'
        ) {
          navigate(routeName.student.studentWorksheet)
        }
      } catch (error) {
        errorHandlerService.handle(error)
      }
    })()
  }, [studentExamId])

  useEffect(() => {
    if (!scoringSize) {
      setSearchParams(
        (prevParams) => {
          if (!prevParams.has('size')) {
            prevParams.append('size', 'all')
          }
          return prevParams
        },
        {
          replace: true,
        },
      )
    }
  }, [scoringSize])

  if (!studentExamId) return null

  const lastScreenProblemIndex = learningContentLastScreenService.getLastScreenProblemIndex([
    'STUDENT_EXAM',
    +studentExamId,
  ])

  const updateLastScreenProblemIndex = (currentProblemIndex: number) => {
    if (studentExamId) {
      learningContentLastScreenService.setLastScreenProblemIndex(
        ['STUDENT_EXAM', +studentExamId],
        currentProblemIndex,
      )
    }
  }

  return (
    <S.StudentWorksheetScoringPage>
      <ExamPageHeader />
      {studentExamPageService.exam &&
        studentExamPageService.problemScoringColl &&
        studentExamPageService.problemScoringViewOption && (
          <>
            {scoringSize === 'all' ? (
              <WorksheetQuickScoring
                isSubmittable={
                  studentExamPageService.studentExamWorksheet?.status === 'PROGRESS' ||
                  studentExamPageService.studentExamWorksheet?.status === 'INCOMPLETE'
                }
                isDeletable={
                  studentExamPageService.studentExamWorksheet?.status === 'PROGRESS' ||
                  studentExamPageService.studentExamWorksheet?.status === 'INCOMPLETE'
                }
                score={studentExamPageService.studentExamWorksheet?.score ?? 0}
                problemScoringColl={studentExamPageService.problemScoringColl}
                problemScoringViewOption={studentExamPageService.problemScoringViewOption}
                gradingCount={studentExamPageService.problemScoringColl.gradingCount}
                clearAllAnswer={() => {
                  studentExamPageService.problemScoringColl.onAllClear((problemScorings) => {
                    studentExamPageService.submitAutoScoringExamProblem(studentExamId, problemScorings)
                  })
                }}
                openConfirmToSubmitAnswersModal={confirmToSubmitAnswers}
                onSubmitAnswerImmediately={(problemScoring) => {
                  studentExamPageService.submitAutoScoringExamProblem(studentExamId, [problemScoring])
                }}
              />
            ) : (
              <S.ScoringByOneWrapper>
                {isMobile ? (
                  <WorksheetScoringByOneMobile
                    problemScoringColl={studentExamPageService.problemScoringColl}
                    viewOption={studentExamPageService.problemScoringViewOption}
                    onSubmit={confirmToSubmitAnswers}
                    studentWorksheet={studentExamPageService.studentExamWorksheet}
                    type={studentExamPageService.exam.type}
                    problemIndex={lastScreenProblemIndex}
                    onProblemIndexChange={updateLastScreenProblemIndex}
                    onSubmitAnswerImmediately={(problemScoring) => {
                      studentExamPageService.submitAutoScoringExamProblem(studentExamId, [
                        problemScoring,
                      ])
                    }}
                  />
                ) : (
                  <WorksheetScoringByOne
                    isSubmittable={
                      studentExamPageService.studentExamWorksheet?.status === 'PROGRESS' ||
                      studentExamPageService.studentExamWorksheet?.status === 'INCOMPLETE'
                    }
                    problemScoringColl={studentExamPageService.problemScoringColl}
                    viewOption={studentExamPageService.problemScoringViewOption}
                    onSubmit={confirmToSubmitAnswers}
                    studentWorksheet={studentExamPageService.studentExamWorksheet}
                    worksheet={studentExamPageService.exam}
                    problemIndex={lastScreenProblemIndex}
                    onProblemIndexChange={updateLastScreenProblemIndex}
                    onSubmitAnswerImmediately={(problemScoring) => {
                      studentExamPageService.submitAutoScoringExamProblem(studentExamId, [
                        problemScoring,
                      ])
                    }}
                  />
                )}
              </S.ScoringByOneWrapper>
            )}
          </>
        )}
    </S.StudentWorksheetScoringPage>
  )
})

export default StudentExamByIdScoringPage

const S = {
  ScoringByOneWrapper: styled.section`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;

    ${mediaQuery.underTablet} {
      padding: 10px 16px 40px;
    }
  `,

  StudentWorksheetScoringPage: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  `,
  StudentWorksheetScoringEmptyFooter: styled.div`
    flex: 0 0 48px;
  `,
  RightLink: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    ${typo.caption01};
    color: ${colors.gray.$900};
    font-weight: bold;
    cursor: pointer;

    ${mediaQuery.underDesktop} {
      ${typo.body02};
      font-weight: normal;
      color: ${colors.gray.$600};
      svg {
        color: ${colors.gray.$600};
      }
    }
  `,
}
